import { Divider, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../../components/shared/PageHeader';
import {
  ReturnToAnalysisBanner,
  buildGroupDetailHref,
  useSessionGroupQuery,
} from '../../../features/session-groups';
import { invalidateSessionRepairCaches } from '../../../features/session-groups/utils/invalidateSessionRepairCaches';
import {
  SessionRepairDetailPanel,
  SessionRepairPreviewModal,
  getAnalysisServiceErrorMessage,
  useSessionRepairDetail,
} from '../../../features/research/packages/session-repair';
import { useAuthUser } from '../../../providers/AuthProvider';

export function SessionGroupSessionDetailPage() {
  const { groupId, sessionId: sessionIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const fromAnalysis = searchParams.get('fromAnalysis');
  const sessionId = Number(sessionIdParam);
  const queryClient = useQueryClient();
  const user = useAuthUser();
  const [previewOpened, setPreviewOpened] = useState(false);
  const { data: group } = useSessionGroupQuery(groupId);

  const {
    isLoading,
    loadError,
    sessionMeta,
    effectiveEvents,
    analysis,
    repairStatus,
    repairPreview,
    isAnalyzing,
    isRepairing,
    runAnalyze,
    runRepair,
  } = useSessionRepairDetail(sessionId);

  const invalidateCaches = async () => {
    if (!Number.isFinite(sessionId)) {
      return;
    }

    await invalidateSessionRepairCaches(queryClient, {
      userId: user?.id?.toString(),
      sessionIds: [sessionId],
      groupId,
    });
  };

  const handleAnalyze = async () => {
    try {
      const result = await runAnalyze();
      await invalidateCaches();

      if (result.status === 'failed') {
        notifications.show({
          color: 'red',
          title: 'Analisi fallita',
          message: result.message ?? 'Analisi non riuscita.',
        });
        return;
      }

      notifications.show({
        color: result.status === 'clean' ? 'teal' : 'orange',
        title: 'Analisi completata',
        message:
          result.summary ??
          (result.status === 'clean'
            ? 'Record perfetto.'
            : 'Sessione da riparare.'),
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Analisi fallita',
        message: getAnalysisServiceErrorMessage(
          error instanceof Error ? error : new Error('Analisi non riuscita.')
        ),
      });
    }
  };

  const handleRepair = async () => {
    try {
      const result = await runRepair();
      await invalidateCaches();

      if (result.status === 'no-op') {
        notifications.show({
          color: 'yellow',
          title: 'Nessuna riparazione necessaria',
          message:
            result.message ||
            'Non sono stati trovati gap o duplicati riparabili.',
        });
        return;
      }

      if (result.status === 'failed' || !result.repairedEvents) {
        notifications.show({
          color: 'red',
          title: 'Riparazione eventi rifiutata',
          message: result.message ?? 'Validazione fallita.',
        });
        return;
      }

      notifications.show({
        color: 'teal',
        title: 'Riparazione completata',
        message: `Eventi originali: ${result.originalCount} → finali: ${result.repairedCount ?? result.repairedEvents.length}`,
      });
      setPreviewOpened(true);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Riparazione fallita',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile riparare la sessione.',
      });
    }
  };

  if (!groupId || !Number.isFinite(sessionId)) {
    return <Text c='red'>Percorso sessione non valido.</Text>;
  }

  const backUrl = buildGroupDetailHref(groupId, fromAnalysis);

  return (
    <>
      <PageHeader
        title={`SESSIONE ${sessionId}`}
        subtitle='Analisi qualità HR, riparazione eventi e confronto prima/dopo.'
        breadcrumbs={[
          { label: 'Sessioni', href: '/sessions' },
          { label: 'Gruppi sessioni', href: '/sessions/group' },
          {
            label: group?.name ?? groupId,
            href: backUrl,
          },
          { label: String(sessionId) },
        ]}
        backUrl={backUrl}
      />

      <Divider my='md' />

      <Stack gap='md'>
        {fromAnalysis && <ReturnToAnalysisBanner analysisId={fromAnalysis} />}

        {isLoading ? (
          <Loader size='sm' />
        ) : loadError ? (
          <Text c='red'>{loadError}</Text>
        ) : (
          <SessionRepairDetailPanel
            sessionId={sessionId}
            patientCode={sessionMeta?.patientCode}
            sessionDate={
              sessionMeta?.started_at ??
              sessionMeta?.created_at ??
              sessionMeta?.updated_at
            }
            scenario={sessionMeta?.scenario}
            repairStatus={repairStatus}
            analysis={analysis}
            effectiveEventsCount={effectiveEvents.length}
            effectiveEvents={effectiveEvents}
            repairPreview={repairPreview}
            isAnalyzing={isAnalyzing}
            isRepairing={isRepairing}
            onAnalyze={() => void handleAnalyze()}
            onRepair={() => void handleRepair()}
            onOpenPreview={() => setPreviewOpened(true)}
          />
        )}
      </Stack>

      <SessionRepairPreviewModal
        opened={previewOpened}
        sessionId={sessionId}
        preview={repairPreview}
        onClose={() => setPreviewOpened(false)}
      />
    </>
  );
}
